import { useAuth } from '@/hooks/useAuth';
import { useImpersonationActive } from '@/hooks/useImpersonationActive';
import { ArrowLeft } from 'lucide-react';

/**
 * AdminModeBar - Sticky banner displayed when an admin is impersonating a user.
 * Shows the impersonated username and provides a button to stop impersonation
 * and return to the admin panel.
 *
 * Visibility is driven by the JWT in localStorage (useImpersonationActive)
 * rather than AuthContext state alone so the banner is guaranteed to render
 * whenever an impersonation token is present, even if React state has not
 * yet caught up (bfcache restore, back-navigation after visiting another
 * site, cross-tab changes).
 */
export function AdminModeBar() {
  const { user, isImpersonating: ctxImpersonating, stopImpersonation } = useAuth();
  const tokenImpersonating = useImpersonationActive();
  const isImpersonating = ctxImpersonating || tokenImpersonating;

  if (!isImpersonating) return null;

  const impersonatedLabel = user?.display_name || user?.username || user?.email || '알 수 없음';

  return (
    <div className="sticky inset-x-0 top-0 z-[80] h-12 shrink-0 border-b border-amber-300 bg-amber-500 text-white shadow-sm">
      <div className="flex h-full items-center justify-between gap-3 px-3 text-sm font-medium md:px-4">
        <span className="min-w-0 truncate">
          관리 모드: <strong>{impersonatedLabel}</strong>
          <span className="hidden sm:inline"> 사용자로 접속 중</span>
        </span>
      <button
        onClick={() => stopImpersonation()}
        className="flex shrink-0 items-center gap-1 rounded bg-white px-3 py-1 text-amber-700 transition-colors hover:bg-amber-50 font-medium"
      >
        <ArrowLeft className="h-4 w-4" />
        돌아가기
      </button>
      </div>
    </div>
  );
}
